import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Loader from "../Loader";
import "./Booking.css";
import AuthContext from "../../contexts/AuthProvider";
import { getUserRole, USER_ROLES } from "../../utils/UsersPermissions";
import { API_URL } from "../../utils/config";
const baseUrl = API_URL;

const BookingDetails = () => {
  const { t, i18n } = useTranslation("booking");
  const navigate = useNavigate();
  const { booking_id } = useParams();
  const [loading, setLoading] = useState(true);
  const [bookingDetails, setBookingDetails] = useState(null);
  const [confirmed, setConfirmed] = useState("pending");
  const [mainOrderSubmitting, setMainOrderSubmitting] = useState(false);
  const [mainOrderErrors, setMainOrderErrors] = useState({});
  const [subOrdersSubmitting, setSubOrdersSubmitting] = useState({});
  const [subOrdersErrors, setSubOrdersErrors] = useState({});
  const [fetchError, setFetchError] = useState(null);
  const [subRequestStatuses, setSubRequestStatuses] = useState({});
  const { isAuthenticated } = useContext(AuthContext);
  const getLangFromLocalStorage = () => {
    return localStorage.getItem("language") || "en";
  };
  const fetchBooking = async () => {
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return null;
      }
      const response = await fetch(
        `${baseUrl}/api/v1/attnd-venue/bookings/${booking_id}`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
        }
      );

      if (response.status === 404) {
        setFetchError(t("errors.notFound"));
        setTimeout(() => navigate("/dashboard"), 1500);
        return null;
      }
      if (response.status === 401) {
        localStorage.removeItem("token");

        navigate("/login");
        return null;
      }
      if (!response.ok) {
        setFetchError(t("errors.fetchFailed"));
        return null;
      }

      const data = await response.json();
      return data.data || null;
    } catch (error) {
      console.error("Error fetching booking details:", error);
      setFetchError(t("errors.network"));
      return null;
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setFetchError(null);
      const details = await fetchBooking();
      if (details) {
        setBookingDetails(details);
        setConfirmed(details.status || "pending");

        // Initialize sub-request statuses if they exist (checking both sub_requests and sub_orders)
        const subRequests = details.sub_requests || details.sub_orders || [];
        if (subRequests.length > 0) {
          const initialStatuses = {};
          subRequests.forEach((subRequest) => {
            initialStatuses[subRequest.id] = subRequest.status || "pending";
          });
          setSubRequestStatuses(initialStatuses);
        }
      }
      setLoading(false);
    })();
  }, [booking_id]);

 

  const handleConfirmation = async (status) => {
    setMainOrderSubmitting(true);
    setMainOrderErrors({});
    try {
      if (!isAuthenticated) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-venue/respondBooking`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
          body: JSON.stringify({ booking_id, status }),
        }
      );
      if (!response.ok) {
        if (response.status === 401) {
          navigate("/login");
          throw new Error(t("errors.unauthorized"));
        }
        const errorData = await response.json();
        let errors = {};
        if (errorData.errors) {
          errors = errorData.errors.reduce((acc, err) => {
            const field = Object.keys(err)[0];
            acc[field] = err[field];
            return acc;
          }, {});
        } else {
          errors.message = errorData.error || t("errors.fetchFailed");
        }
        setMainOrderErrors(errors);
        throw new Error("Failed to update booking status");
      }
      await response.json();
      setConfirmed(status);
    } catch (err) {
      console.error("Confirmation error:", err);
      if (!mainOrderErrors.message) {
        setMainOrderErrors({ message: err.message });
      }
    } finally {
      setMainOrderSubmitting(false);
    }
  };

  const handleSubRequestConfirmation = async (subRequestId, status) => {
    // Set submitting state for this specific sub-order
    setSubOrdersSubmitting((prev) => ({
      ...prev,
      [subRequestId]: true,
    }));
    
    // Clear any existing errors for this specific sub-order
    setSubOrdersErrors((prev) => ({
      ...prev,
      [subRequestId]: {},
    }));

    try {
      if (!isAuthenticated) {
        navigate("/login");
        return;
      }

      const response = await fetch(
        `${baseUrl}/api/v1/attnd-venue/respondSubOrder`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            lang: getLangFromLocalStorage(),
          },
          body: JSON.stringify({ sub_order_id: subRequestId, status: status }),
        }
      );

      if (!response.ok) {
        if (response.status === 401) {
          navigate("/login");
          throw new Error("Unauthorized");
        }
        const errorData = await response.json();
        let errors = {};
        if (errorData.errors) {
          errors = errorData.errors.reduce((acc, err) => {
            const field = Object.keys(err)[0];
            acc[field] = err[field];
            return acc;
          }, {});
        } else {
          errors.message =
            errorData.error || "Failed to update sub-request status";
        }
        
        // Set errors for this specific sub-order
        setSubOrdersErrors((prev) => ({
          ...prev,
          [subRequestId]: errors,
        }));
        throw new Error("Failed to update sub-request status");
      }

      await response.json();
      // Update the specific sub-request status
      setSubRequestStatuses((prev) => ({
        ...prev,
        [subRequestId]: status,
      }));
    } catch (err) {
      console.error("Sub-request confirmation error:", err);
      // Set error for this specific sub-order if not already set
      setSubOrdersErrors((prev) => {
        const currentErrors = prev[subRequestId] || {};
        if (!currentErrors.message) {
          return {
            ...prev,
            [subRequestId]: { message: err.message },
          };
        }
        return prev;
      });
    } finally {
      // Clear submitting state for this specific sub-order
      setSubOrdersSubmitting((prev) => ({
        ...prev,
        [subRequestId]: false,
      }));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("defaults.unknown");
    try {
      return new Date(dateString)
        .toLocaleString(i18n.language==="ar"?"ar-EG":"en-GB"
          , {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        .replace(",", " -");
    } catch {
      return t("defaults.invalidDate");
    }
  };

  const formatCurrency = (
    amount,
    perDay = false,
    perHour = false,
    perAttendee = false,
    perItem = false
  ) => {
    if (amount == null) return t("defaults.unknown");
    let suffix = "";

    // Check combinations (per person/attendee is for venue only)
    if (perDay && perAttendee) {
      suffix = t("financial.costSuffix.perDayPerPerson");
    } else if (perHour && perAttendee) {
      suffix = t("financial.costSuffix.perHourPerPerson");
    } else if (perDay) {
      suffix = t("financial.costSuffix.perDay");
    } else if (perHour) {
      suffix = t("financial.costSuffix.perHour");
    } else if (perItem) {
      suffix = t("financial.costSuffix.perItem");
    }

    return (
      Number(amount).toLocaleString(i18n.language === "ar" ? "ar-EG" : "en-GB") +
      t("currencySymbol") +
      (suffix ? suffix : "")
    );
  };

  // Derive venue cost unit from additions (venue_pricing_snapshot or fallback to venue_rate)
  // If daily_rate is null/empty → hourly; if it has a value → daily. Per-attendee is venue only.
  const additions = bookingDetails?.order?.additions ?? {};
  const snapshot = additions.venue_pricing_snapshot;
  const isVenueHourly = snapshot
    ? snapshot.daily_rate == null || snapshot.daily_rate === ""
    : additions.venue_rate === "hourly";
  const isVenueDaily = snapshot
    ? snapshot.daily_rate != null && snapshot.daily_rate !== ""
    : !isVenueHourly;
  const isVenuePerAttendee = Boolean(snapshot?.per_attendees);

  if (loading) {
    return (
      <div className="booking-loader-container">
        <Loader />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="booking-details">
        <div className="error-message">
          <h3>{fetchError}</h3>
          <p>{t("headings.redirecting")}</p>
        </div>
      </div>
    );
  }

  if (!bookingDetails) {
    return (
      <div className="booking-details">
        <div className="error-message">
          <h3>{t("headings.noData")}</h3>
          <button
            onClick={() => navigate("/dashboard")}
            className="back-button"
          >
            {t("buttons.backToDashboard")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-details">
      <div className="details-header">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <h2 className="booking-details-heading">{t("headings.pageTitle")}</h2>
          {/* Hide message button for viewers */}
          {getUserRole() !== USER_ROLES.VIEWER && (
            <button
              onClick={() =>
                navigate("/messages", { state: { orderId: booking_id } })
              }
              style={{
                padding: "12px",
                backgroundColor: "#5007C5",
                color: "white",
                border: "none",
                borderRadius: "12px",
                cursor: "pointer",
                fontSize: "18px",
                fontWeight: "700",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                transition: "background-color 0.2s",
              }}
              onMouseEnter={(e) => (e.target.style.backgroundColor = "#5007C5")}
              onMouseLeave={(e) => (e.target.style.backgroundColor = "#5007C5")}
            >
              {t("buttons.message") || "Message"}
              <svg
                width="18"
                height="19"
                viewBox="0 0 18 19"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M16.5 6.07227V10.5723C16.5 11.6973 16.125 12.6348 15.465 13.2948C14.8125 13.9473 13.875 14.3223 12.75 14.3223V15.9198C12.75 16.5198 12.0825 16.8798 11.5875 16.5498L8.25 14.3223H6.66C6.72 14.0973 6.75 13.8648 6.75 13.6248C6.75 12.8598 6.4575 12.1548 5.9775 11.6223C5.4375 11.0073 4.635 10.6248 3.75 10.6248C2.91 10.6248 2.145 10.9698 1.5975 11.5323C1.53 11.2323 1.5 10.9098 1.5 10.5723V6.07227C1.5 3.82227 3 2.32227 5.25 2.32227H12.75C15 2.32227 16.5 3.82227 16.5 6.07227Z"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M6.75 13.625C6.75 13.865 6.72 14.0975 6.66 14.3225C6.5925 14.6225 6.47251 14.915 6.31501 15.17C5.79751 16.04 4.845 16.625 3.75 16.625C2.9775 16.625 2.28 16.3325 1.755 15.8525C1.53 15.6575 1.33499 15.425 1.18499 15.17C0.90749 14.72 0.75 14.1875 0.75 13.625C0.75 12.815 1.0725 12.0725 1.5975 11.5325C2.145 10.97 2.91 10.625 3.75 10.625C4.635 10.625 5.4375 11.0075 5.9775 11.6225C6.4575 12.155 6.75 12.86 6.75 13.625Z"
                  fill="white"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M4.86731 13.6094H2.63232"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M3.75 12.5146V14.7571"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M6.375 8.375H11.625"
                  stroke="white"
                  stroke-width="1.5"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          )}
        </div>
        <h3 className="details-heading">
          {(bookingDetails.venue_name
            ? bookingDetails.venue_name
            : t("defaults.venueNameUnavailable"))} &nbsp;
          <span>
            {bookingDetails.order && bookingDetails.order.id
              ? `#ORD-${bookingDetails.order.id}`
              : ""}
          </span>
        </h3>
        {/* location */}
        <div className="booking-location">
          <p>{bookingDetails.venue_location}</p>
        </div>

        <div className="booking-name">
          {bookingDetails.venue_images &&
          bookingDetails.venue_images.length > 0 ? (
            <img
              style={{
                width: "40px",
                height: "40px",
                borderRadius: "50%",
                objectFit: "cover",
              }}
              src={bookingDetails.venue_images[0]}
              alt="booking"
            />
          ) : (
            <svg
              width="40"
              height="40"
              viewBox="0 0 40 40"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              {/* SVG paths unchanged */}
            </svg>
          )}
          <p>{bookingDetails.venue_brand}</p>
        </div>

        <div className="booking-info">
          <div className="info-card">
            <h4>{t("infoCards.startDate")}</h4>
            <div className="info-content">
              <img src="/booking-calendar.svg" alt={t("alt.calendar")} />
              <p>{formatDate(bookingDetails.start)}</p>
            </div>
          </div>
          <div className="info-card">
            <h4>{t("infoCards.endDate")}</h4>
            <div className="info-content">
              <img src="/booking-calendar.svg" alt={t("alt.calendar")} />
              <p>{formatDate(bookingDetails.end)}</p>
            </div>
          </div>
          <div className="info-card attendee-card">
            <h4>{t("infoCards.attendees")}</h4>
            <div className="info-content">
              <img src="/people.svg" alt={t("alt.people")} />
              <p>{bookingDetails.attendees || t("defaults.unknown")}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-Request Details Section */}
      {((bookingDetails.sub_requests &&
        bookingDetails.sub_requests.length > 0) ||
        (bookingDetails.sub_orders &&
          bookingDetails.sub_orders.length > 0)) && (
        <div className="sub-requests-container">
          <h3 className="financial-heading">
            {t("subRequests.detailsHeading")}
          </h3>

          {(bookingDetails.sub_requests || bookingDetails.sub_orders || []).map(
            (subRequest, index) => {
              const status =
                subRequestStatuses[subRequest.id] ||
                subRequest.status ||
                "pending";
              const subtotal = parseFloat(
                subRequest.sub_total ||
                  subRequest.total ||
                  subRequest.total_cost ||
                  subRequest.cost ||
                  subRequest.price ||
                  0
              );
              const vatAmount = parseFloat(subRequest.tax || subtotal * 0.15);
              const totalAmount = parseFloat(
                subRequest.total || subtotal + vatAmount
              );

              return (
                <div
                  key={subRequest.id || index}
                  className="financial-details"
                  style={{ marginBottom: "30px" }}
                >
                  {/* <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "20px",
                      borderBottom: "2px solid #e5e7eb",
                      paddingBottom: "15px",
                    }}
                  >
                    <h4
                      style={{
                        color: "#374151",
                        fontSize: "18px",
                        fontWeight: "600",
                        margin: "0",
                      }}
                    ></h4>

                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span
                        className={`status-badge ${
                          status === "accepted" && "confirmed-booking"
                        } ${status === "rejected" && "rejected"} ${
                          status === "pending" && "pending-status"
                        }`}
                        style={{ fontSize: "14px", padding: "4px 12px" }}
                      >
                        {t(`badgeStatus.${status}`)}
                      </span>
                    </div>
                  </div> */}

                  {/* Sub Request Details Table */}
                  <div className="booking-table table-wrapper">
                    <table className="bookings-table details">
                      <thead>
                        <tr>
                          <th>{t("subRequests.tableHeaders.venue")}</th>
                          <th>{t("subRequests.tableHeaders.submittedOn")}</th>
                          <th>{t("subRequests.tableHeaders.numAttendees")}</th>
                          <th>{t("subRequests.tableHeaders.cost")}</th>
                          <th>{t("subRequests.tableHeaders.total")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            {subRequest.venue_name ||
                              bookingDetails.venue_name ||
                              t("defaults.unknown")}
                          </td>
                          <td>
                            {formatDate(
                              subRequest.submitted_on ||
                                subRequest.created_at ||
                                subRequest.responded_at
                            )}
                          </td>
                          <td>
                            {subRequest.num_attendees ||
                              subRequest.attendees ||
                              t("defaults.unknown")}
                          </td>
                          <td>{formatCurrency(subtotal)}</td>
                          <td>{formatCurrency(subtotal)}</td>
                        </tr>

                        {/* Financial Summary for this sub-request */}
                        <tr
                          style={{
                            borderTop: "2px solid var(--Primary-Dark, #5507CF)",
                          }}
                        >
                          <td>{t("financial.rows.subtotal")}</td>
                          <td colSpan="3" />
                          <td>{formatCurrency(subtotal)}</td>
                        </tr>
                        {/* fees if found */}
                        {subRequest.attnd_fees != null && (
                          <tr>
                            <td>{t("financial.rows.attndFees")}</td>
                            <td colSpan="3" />
                            <td>
                              {formatCurrency(subRequest.attnd_fees)}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td>{t("financial.rows.vat")}</td>
                          <td colSpan="3" />
                          <td>{formatCurrency(vatAmount)}</td>
                        </tr>
                        <tr
                          style={{
                            borderTop: "2px solid var(--Primary-Dark, #5507CF)",
                            borderBottom:
                              "2px solid var(--Primary-Dark, #5507CF)",
                          }}
                        >
                          <td style={{ fontSize: "18px", fontWeight: "600" }}>
                            {t("financial.tableHeaders.total")}
                          </td>
                          <td colSpan="3" />
                          <td style={{ fontSize: "18px", fontWeight: "600" }}>
                            {formatCurrency(totalAmount)}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      justifyContent: subRequest.notes
                        ? "space-between"
                        : "flex-end",
                      alignItems: "center",
                      marginTop: "20px",
                    }}
                  >
                    {/* Notes Section */}
                    {subRequest.notes && (
                      <div className="booking-notes">
                        <h4>{t("financial.note")}</h4>
                        <p>{subRequest.notes}</p>
                      </div>
                    )}

                    {/* Action Buttons - Hide for viewers */}
                    {getUserRole() !== USER_ROLES.VIEWER && (
                      <div className="booking-actions-btns">
                        {status === "pending" && (
                          <>
                            <button
                              className="reject-btn"
                              onClick={() =>
                                handleSubRequestConfirmation(
                                  subRequest.id,
                                  "rejected"
                                )
                              }
                              disabled={subOrdersSubmitting[subRequest.id] || false}
                              style={{ marginRight: "10px" }}
                            >
                              {subOrdersSubmitting[subRequest.id]
                                ? t("buttons.processing")
                                : t("buttons.reject")}
                            </button>
                            <button
                              className="confirm-btn"
                              onClick={() =>
                                handleSubRequestConfirmation(
                                  subRequest.id,
                                  "accepted"
                                )
                              }
                              disabled={subOrdersSubmitting[subRequest.id] || false}
                            >
                              {subOrdersSubmitting[subRequest.id]
                                ? t("buttons.processing")
                                : t("buttons.confirm")}
                            </button>
                          </>
                        )}
                        {status === "accepted" && (
                          <p className="confirmed-btn">
                            {t("badgeStatus.accepted")}
                          </p>
                        )}
                        {status === "rejected" && (
                          <p className="rejected-btn">
                            {t("badgeStatus.rejected")}
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Error Messages for this specific sub-request */}
                  {subOrdersErrors[subRequest.id]?.message && (
                    <p className="error-messages" style={{ margin: "15px 0" }}>
                      {subOrdersErrors[subRequest.id].message}
                    </p>
                  )}
                </div>
              );
            }
          )}

          {/* Overall Sub-Orders Summary */}
          {/* <div className="financial-details">
            <h4
              style={{
                color: "#5507cf",
                fontSize: "18px",
                fontWeight: "600",
                marginBottom: "15px",
              }}
            >
              Sub-Orders Summary
            </h4>
            <div className="booking-table table-wrapper">
              <table className="bookings-table details">
                <tbody>
                  <tr
                    style={{
                      borderTop: "2px solid var(--Primary-Dark, #5507CF)",
                    }}
                  >
                    <td style={{ fontSize: "16px", fontWeight: "500" }}>
                      Total Sub-Orders (
                      {
                        (
                          bookingDetails.sub_requests ||
                          bookingDetails.sub_orders ||
                          []
                        ).length
                      }
                      )
                    </td>
                    <td colSpan="3" />
                    <td style={{ fontSize: "16px", fontWeight: "500" }}>
                      {formatCurrency(
                        (
                          bookingDetails.sub_requests ||
                          bookingDetails.sub_orders ||
                          []
                        ).reduce((sum, req) => {
                          const amount = parseFloat(
                            req.total ||
                              req.total_cost ||
                              req.sub_total ||
                              req.cost ||
                              req.price ||
                              0
                          );
                          return sum + amount;
                        }, 0)
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div> */}
        </div>
      )}

      <div className="financial-details">
        <h3 className="financial-heading">{t("financial.venueHeading")}</h3>
        <div className="booking-table table-wrapper">
          <table className="bookings-table details">
            <thead>
              <tr>
                <th>{t("financial.tableHeaders.items")}</th>
                <th>{t("financial.tableHeaders.dates")}</th>
                <th>{t("financial.tableHeaders.attendees")}</th>
                <th>{t("financial.tableHeaders.cost")}</th>
                <th>{t("financial.tableHeaders.total")}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t("tableHeaders.venue")}</td>
                <td>{formatDate(bookingDetails.start)}</td>
                <td>{bookingDetails.attendees || t("defaults.unknown")}</td>
                <td>
                  {formatCurrency(
                    bookingDetails.order.additions.venue_price,
                    isVenueDaily,
                    isVenueHourly,
                    isVenuePerAttendee,
                    false
                  )}
                </td>
                <td>
                  {formatCurrency(bookingDetails.order.additions.venue_cost)}
                </td>
              </tr>
              {(bookingDetails.order.additions.catering || []).map((item, i) => (
                <tr key={`catering-${item.id ?? i}-${i}`}>
                  <td>{item.dish_name || item.name || t("defaults.unknown")}</td>
                  <td>{formatDate(bookingDetails.start)}</td>
                  <td>{bookingDetails.attendees || t("defaults.unknown")}</td>
                  <td>
                    {formatCurrency(item.cost, false, false, false, true)}
                  </td>
                  <td>{formatCurrency(item.totalForCatering)}</td>
                </tr>
              ))}
              {(bookingDetails.order.additions.facilities || []).map((item, i) => {
                const facilityCost = item.cost != null ? item.cost : item.price;
                const facilityPerDay = item.duration === "day";
                const facilityPerHour = item.duration === "hour";
                return (
                  <tr key={`facility-${item.id ?? i}-${i}`}>
                    <td>{item.tool_name || item.name || t("defaults.unknown")}</td>
                    <td>{formatDate(bookingDetails.start)}</td>
                    <td>{bookingDetails.attendees || t("defaults.unknown")}</td>
                    <td>
                      {formatCurrency(
                        facilityCost,
                        facilityPerDay,
                        facilityPerHour,
                        false,
                        false
                      )}
                    </td>
                    <td>{formatCurrency(item.totalForFacility)}</td>
                  </tr>
                );
              })}
              <tr
                style={{ borderTop: "2px solid var(--Primary-Dark, #5507CF)" }}
              >
                <td>{t("financial.rows.subtotal")}</td>
                <td colSpan="3" />
                <td>{formatCurrency(bookingDetails.order.sub_total)}</td>
              </tr>
              <tr>
                <td>{t("financial.rows.attndFees")}</td>
                <td colSpan="3" />
                <td>{formatCurrency(bookingDetails.order.attnd_fees)}</td>
              </tr>
              <tr>
                <td>{t("financial.rows.vat")}</td>
                <td colSpan="3" />
                <td>{formatCurrency(bookingDetails.order.tax)}</td>
              </tr>
              <tr
                style={{
                  borderTop: "2px solid var(--Primary-Dark, #5507CF)",
                  borderBottom: "2px solid var(--Primary-Dark, #5507CF)",
                }}
              >
                <td style={{ fontSize: "20px" }}>
                  {t("financial.tableHeaders.total")}
                </td>
                <td colSpan="3" />
                <td>{formatCurrency(bookingDetails.order.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: bookingDetails.order.notes
              ? "space-between"
              : "flex-end",
            alignItems: "center",
            marginTop: "20px",
          }}
        >
          {bookingDetails.order.notes && (
            <div className="booking-notes">
              <h4>{t("financial.note")}</h4>
              <p>{bookingDetails.order.notes}</p>
            </div>
          )}

          {/* Hide action buttons for viewers */}
          {getUserRole() !== USER_ROLES.VIEWER && (
            <div className="booking-actions-btns">
              {confirmed === "pending" && (
                <>
                  <button
                    className="reject-btn"
                    onClick={() => handleConfirmation("rejected")}
                    disabled={mainOrderSubmitting}
                  >
                    {mainOrderSubmitting ? t("buttons.processing") : t("buttons.reject")}
                  </button>
                  <button
                    className="confirm-btn"
                    onClick={() => handleConfirmation("accepted")}
                    disabled={mainOrderSubmitting}
                  >
                    {mainOrderSubmitting
                      ? t("buttons.processing")
                      : t("buttons.confirm")}
                  </button>
                </>
              )}
              {confirmed === "accepted" && (
                <p className="confirmed-btn">{t("labels.confirmed")}</p>
              )}
                {confirmed === "paid" && (
                <p className="confirmed-btn">{t("labels.paid")}</p>
              )}
              {confirmed === "rejected" && (
                <p className="rejected-btn">{t("labels.rejected")}</p>
              )}
            </div>
          )}
        </div>

        {mainOrderErrors.message && (
          <p className="error-messages" style={{ margin: "15px 0" }}>
            {mainOrderErrors.message}
          </p>
        )}
      </div>
    </div>
  );
};

export default BookingDetails;
